import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "../../contexts/AuthContext";
import useVatMarginScheme from "../../data/useVatMarginScheme";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { IVehicle } from "../../models/Vehicle";

/**
 * Admin control for the vehicle-level purchase VAT flag (the source of truth — leads mirror it).
 * Parts-only customers have no leads, so this is where their vehicles' VAT status is set.
 * Changing it re-stamps the sales treatment and margin pool when the Global Margin Scheme is on.
 */
export default function VehiclePurchaseVat({ vehicle }: { vehicle: IVehicle }) {
  const { t } = useTranslation("vehicles");
  const { claims } = useAuth();
  const { setVehiclePurchaseVat } = useVatMarginScheme();
  const { saveHandlers } = useDefaultCRUDHandlers(t("Vehicle purchase VAT"));

  const [pendingValue, setPendingValue] = useState<boolean | null>(null);
  const [purchaseCost, setPurchaseCost] = useState("");

  const currentValue = vehicle.applyVAT ?? false;
  const editing = pendingValue !== null && pendingValue !== currentValue;

  const handleCancel = () => {
    setPendingValue(null);
    setPurchaseCost("");
  };

  const handleSave = () => {
    if (pendingValue === null) return;

    setVehiclePurchaseVat.mutate(
      {
        vehicleId: vehicle.id,
        applyVat: pendingValue,
        purchaseCost: purchaseCost ? parseFloat(purchaseCost) : null,
      },
      {
        onSuccess: () => {
          handleCancel();
          saveHandlers.onSuccess();
        },
        onError: () => saveHandlers.onError(),
      }
    );
  };

  if (!claims?.isAdmin) {
    return (
      <div className="py-2 grid grid-cols-3 gap-4">
        <dt className="text-sm font-medium text-gray-500">{t("VAT on purchase")}</dt>
        <dd className="text-gray-900 text-sm col-span-2">{currentValue ? t("Yes") : t("No")}</dd>
      </div>
    );
  }

  return (
    <div className="py-2 grid grid-cols-3 gap-4 items-start">
      <dt className="text-sm font-medium text-gray-500">{t("VAT on purchase")}</dt>
      <dd className="col-span-2 space-y-2">
        <select
          className="w-full text-sm border-gray-300 rounded-md focus:border-primary-500 focus:ring-primary-500"
          value={String(pendingValue ?? currentValue)}
          disabled={setVehiclePurchaseVat.isLoading}
          onChange={(e) => {
            const next = e.target.value === "true";
            setPendingValue(next === currentValue ? null : next);
          }}
        >
          <option value="true">{t("Yes - VAT charged (trade seller)")}</option>
          <option value="false">{t("No - no VAT (private seller)")}</option>
        </select>

        {editing && (
          <>
            {pendingValue === false && (
              <div>
                <label className="block text-xs font-medium text-gray-500">
                  {t("Margin pool purchase cost (optional)")}
                </label>
                <input
                  value={purchaseCost}
                  onChange={(e) => setPurchaseCost(e.target.value)}
                  className="mt-1 w-full text-sm"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={t("Defaults to the lead price when one exists")}
                />
              </div>
            )}

            <p className="text-xs text-amber-600">
              {pendingValue
                ? t("With the margin scheme enabled, sales become standard-rated and any open margin pool entry is removed.")
                : t("With the margin scheme enabled, sales use the margin scheme and the vehicle enters the margin pool.")}
            </p>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={setVehiclePurchaseVat.isLoading}
                className="rounded-md border border-transparent bg-primary-600 px-3 py-1.5 text-xs font-medium leading-4 text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
              >
                {t("Save")}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={setVehiclePurchaseVat.isLoading}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
              >
                {t("Cancel")}
              </button>
            </div>
          </>
        )}
      </dd>
    </div>
  );
}
